// src/components/layout/SubmissionsList.jsx
import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  FileText,
  Calendar,
  Users,
  PlusCircle,
  TrendingUp,
  AlertCircle,
  Grid3X3,
  List,
  ChevronDown,
  ChevronUp,
  X,
  BookOpen,
  BarChart3,
  Clock,
  CheckCircle2,
  XCircle,
  Calculator
} from 'lucide-react';
import { AssessmentService } from '../../services/taskService';
import { useAuth } from '../../contexts/AuthContext';
import { useAssessmentService } from '../../hooks/useTaskService';

const SubmissionsList = ({ onSubmissionSelect, onEditSubmission, onCreateSubmission, onRecordMarks }) => {
  const { currentUser } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [tasks, setTasks] = useState([]);
  const { loading, error, getSubmissions, getTasks, clearError } = useAssessmentService();
  
  // Filter and view states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterGrade, setFilterGrade] = useState('all');
  const [filterTerm, setFilterTerm] = useState('all');
  const [filterSubject, setFilterSubject] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'

  // Statistics state
  const [stats, setStats] = useState({
    totalSubmissions: 0,
    averageSubmissionRate: 0,
    thisWeekSubmissions: 0,
    pendingMarks: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [submissions, searchTerm, filterType, filterGrade, filterTerm, filterSubject, dateRange]);

  const loadData = async () => {
    try {
      const [submissionList, taskList] = await Promise.all([
        getSubmissions(),
        getTasks()
      ]);
      setSubmissions(submissionList);
      setTasks(taskList);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };

  const handleDeleteSubmission = async (submissionId, taskName) => {
    if (window.confirm(`Are you sure you want to delete the submission for "${taskName}"?`)) {
      try {
        // Note: You'll need to add deleteTaskSubmission to AssessmentService
        await AssessmentService.deleteTaskSubmission(submissionId);
        setSubmissions(prev => prev.filter(sub => sub.id !== submissionId));
      } catch (err) {
        console.error('Failed to delete submission:', err);
        alert('Failed to delete submission. Please try again.');
      }
    }
  };

  const applyFilters = () => {
    let filtered = submissions.filter(submission => {
      const task = tasks.find(t => t.id === submission.taskId);
      const taskName = task?.name || '';
      
      const matchesSearch = taskName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           submission.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           submission.topics.some(topic => topic.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           submission.grade.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = filterType === 'all' || task?.type === filterType;
      const matchesGrade = filterGrade === 'all' || submission.grade === filterGrade;
      const matchesTerm = filterTerm === 'all' || submission.term === filterTerm;
      const matchesSubject = filterSubject === 'all' || submission.subject === filterSubject;
      
      // Date range filter
      const matchesDateRange = !dateRange.start || !dateRange.end || 
        (new Date(submission.date) >= new Date(dateRange.start) && 
         new Date(submission.date) <= new Date(dateRange.end));
      
      return matchesSearch && matchesType && matchesGrade && matchesTerm && matchesSubject && matchesDateRange;
    });
    
    // Sort by date (most recent first)
    filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    setFilteredSubmissions(filtered);
  };

  const calculateStats = () => {
    const total = submissions.length;
    const averageRate = submissions.length > 0 
      ? submissions.reduce((sum, sub) => sum + getSubmissionRate(sub), 0) / submissions.length
      : 0;
    
    // This week submissions
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const thisWeek = submissions.filter(sub => new Date(sub.date) >= oneWeekAgo).length;
    
    // Pending marks (submissions without marks recorded)
    // This would need to be calculated by checking if marks exist for each submission
    const pending = submissions.filter(sub => !sub.marksRecorded).length;
    
    setStats({
      totalSubmissions: total,
      averageSubmissionRate: averageRate,
      thisWeekSubmissions: thisWeek,
      pendingMarks: pending
    });
  };

  const getSubmissionRate = (submission) => {
    return submission.expectedSubmissions > 0 
      ? (submission.actualSubmissions / submission.expectedSubmissions) * 100 
      : 0;
  };

  const getTaskName = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    return task?.name || 'Unknown Task';
  };

  const getTaskType = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    return task?.type || 'Unknown';
  };

  const getUniqueValues = (key) => {
    return [...new Set(submissions.map(sub => sub[key]))].filter(Boolean).sort();
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    setFilterGrade('all');
    setFilterTerm('all');
    setFilterSubject('all');
    setDateRange({ start: '', end: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Submissions</h3>
        <p className="text-gray-500 mb-4">{error}</p>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full">
      {/* Header with Statistics */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Task Submissions</h2>
            <p className="text-gray-600">Track and manage assessment submissions</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Record Submission Button */}
            <button
              onClick={onCreateSubmission}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Record Submission
            </button>

            <div className="text-sm text-gray-500">
              {filteredSubmissions.length} of {submissions.length} submissions
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('card')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'card' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Card view"
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="List view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Filters Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              <Filter className="w-4 h-4" />
              Filters
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <h3 className="font-medium text-blue-800">Total Submissions</h3>
            </div>
            <p className="text-2xl font-bold text-blue-900">{stats.totalSubmissions}</p>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <h3 className="font-medium text-green-800">Avg. Submission Rate</h3>
            </div>
            <p className="text-2xl font-bold text-green-900">{stats.averageSubmissionRate.toFixed(1)}%</p>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-yellow-600" />
              <h3 className="font-medium text-yellow-800">This Week</h3>
            </div>
            <p className="text-2xl font-bold text-yellow-900">{stats.thisWeekSubmissions}</p>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Calculator className="w-5 h-5 text-purple-600" />
              <h3 className="font-medium text-purple-800">Pending Marks</h3>
            </div>
            <p className="text-2xl font-bold text-purple-900">{stats.pendingMarks}</p>
          </div>
        </div>
      </div>

      {/* Collapsible Filters */}
      {showFilters && (
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search submissions..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Task Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="Assignment">Assignment</option>
                <option value="Project">Project</option>
                <option value="Investigation">Investigation</option>
                <option value="Test">Test</option>
                <option value="Exam">Exam</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
              <select
                value={filterGrade}
                onChange={(e) => setFilterGrade(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Grades</option>
                {getUniqueValues('grade').map(grade => (
                  <option key={grade} value={grade}>{grade}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
              <select
                value={filterTerm}
                onChange={(e) => setFilterTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Terms</option>
                {getUniqueValues('term').map(term => (
                  <option key={term} value={term}>{term}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <select
                value={filterSubject}
                onChange={(e) => setFilterSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Subjects</option>
                {getUniqueValues('subject').map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              {(searchTerm || filterType !== 'all' || filterGrade !== 'all' || filterTerm !== 'all' || 
                filterSubject !== 'all' || dateRange.start || dateRange.end) && (
                <button
                  onClick={clearAllFilters}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                >
                  Clear All
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Hide Filters
            </button>
          </div>
        </div>
      )}

      {/* Submissions Display */}
      {viewMode === 'card' ? (
        /* Card View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSubmissions.map(submission => (
            <SubmissionCard 
              key={submission.id}
              submission={submission}
              taskName={getTaskName(submission.taskId)}
              taskType={getTaskType(submission.taskId)}
              onSubmissionSelect={onSubmissionSelect}
              onEditSubmission={onEditSubmission}
              onDeleteSubmission={handleDeleteSubmission}
              onRecordMarks={onRecordMarks}
            />
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Task & Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Grade/Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Term
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submissions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSubmissions.map(submission => (
                  <SubmissionRow 
                    key={submission.id}
                    submission={submission}
                    taskName={getTaskName(submission.taskId)}
                    taskType={getTaskType(submission.taskId)}
                    onSubmissionSelect={onSubmissionSelect}
                    onEditSubmission={onEditSubmission}
                    onDeleteSubmission={handleDeleteSubmission}
                    onRecordMarks={onRecordMarks}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredSubmissions.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {submissions.length === 0 ? 'No task submissions yet' : 'No submissions match your filters'}
          </h3>
          <p className="text-gray-500 mb-6">
            {submissions.length === 0 
              ? 'Record your first task submission to get started.'
              : 'Try adjusting your search terms or filters.'
            }
          </p>
          {submissions.length === 0 && (
            <button
              onClick={onCreateSubmission}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2 mx-auto"
            >
              <PlusCircle className="w-4 h-4" />
              Record First Submission
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Submission Card Component
const SubmissionCard = ({ submission, taskName, taskType, onSubmissionSelect, onEditSubmission, onDeleteSubmission, onRecordMarks }) => {
  const submissionRate = submission.expectedSubmissions > 0 
    ? (submission.actualSubmissions / submission.expectedSubmissions) * 100 
    : 0;

  const nonSubmissions = submission.expectedSubmissions - submission.actualSubmissions - submission.absentees;

  return (
    <div className="bg-blue-50 rounded-lg shadow hover:shadow-md transition-shadow">
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">{taskName}</h3>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                taskType === 'Exam' ? 'bg-red-100 text-red-800' :
                taskType === 'Test' ? 'bg-orange-100 text-orange-800' :
                taskType === 'Project' ? 'bg-purple-100 text-purple-800' :
                taskType === 'Assignment' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {taskType}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                submissionRate >= 90 ? 'bg-green-100 text-green-800' :
                submissionRate >= 70 ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {submissionRate.toFixed(1)}% submitted
              </span>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>{new Date(submission.date).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <BookOpen className="w-4 h-4" />
            <span>{submission.grade} • {submission.subject} • {submission.term}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Users className="w-4 h-4" />
            <span>
              {submission.actualSubmissions}/{submission.expectedSubmissions} learners
              {submission.absentees > 0 && ` (${submission.absentees} absent)`}
            </span>
          </div>
        </div>

        {/* Submission Statistics */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-lg font-semibold text-green-600">{submission.actualSubmissions}</div>
              <div className="text-xs text-gray-500">Submitted</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-red-600">{nonSubmissions}</div>
              <div className="text-xs text-gray-500">Missing</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-yellow-600">{submission.absentees}</div>
              <div className="text-xs text-gray-500">Absent</div>
            </div>
          </div>
        </div>

        {/* Topics */}
        {submission.topics && submission.topics.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-1">
              {submission.topics.slice(0, 3).map((topic, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                >
                  {topic}
                </span>
              ))}
              {submission.topics.length > 3 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                  +{submission.topics.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => onSubmissionSelect(submission)}
            className="px-3 py-2 bg-gray-100 text-purple-700 text-sm rounded-md hover:bg-gray-200 flex items-center justify-center"
          >
            <Eye className="w-4 h-4" />
          </button>
          
          {onRecordMarks && (
            <button
              onClick={() => onRecordMarks(submission)}
              className="px-3 py-2 bg-gray-100 text-purple-700 text-sm rounded-md hover:bg-gray-200 flex items-center justify-center"
              title="Record marks"
            >
              <Calculator className="w-4 h-4" />
            </button>
          )}
          
          <button
            onClick={() => onEditSubmission(submission)}
            className="px-3 py-2 bg-gray-100 text-purple-700 text-sm rounded-md hover:bg-gray-200 flex items-center justify-center"
            title="Edit submission"
          >
            <Edit className="w-4 h-4" />
          </button>
          
          <button
            onClick={() => onDeleteSubmission(submission.id, taskName)}
            className="px-3 py-2 bg-red-100 text-red-700 text-sm rounded-md hover:bg-red-200 flex items-center justify-center"
            title="Delete submission"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        {/* Status indicator */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Created {new Date(submission.createdAt).toLocaleDateString()}
            </p>
            <div className="flex items-center gap-1">
              {submission.marksRecorded ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" title="Marks recorded" />
              ) : (
                <XCircle className="w-4 h-4 text-orange-500" title="Marks pending" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Submission Row Component for List View
const SubmissionRow = ({ submission, taskName, taskType, onSubmissionSelect, onEditSubmission, onDeleteSubmission, onRecordMarks }) => {
  const submissionRate = submission.expectedSubmissions > 0 
    ? (submission.actualSubmissions / submission.expectedSubmissions) * 100 
    : 0;

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4">
        <div>
          <div className="text-sm font-medium text-gray-900">{taskName}</div>
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <Calendar className="w-3 h-3" />
            {new Date(submission.date).toLocaleDateString()}
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-gray-900">{submission.grade}</div>
        <div className="text-sm text-gray-500">{submission.subject}</div>
      </td>
      <td className="px-6 py-4 text-sm text-gray-900">
        {submission.term}
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-gray-900">
          {submission.actualSubmissions}/{submission.expectedSubmissions}
        </div>
        <div className="text-xs text-gray-500">
          {submission.absentees} absent
        </div>
      </td>
      <td className="px-6 py-4">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          submissionRate >= 90 ? 'bg-green-100 text-green-800' :
          submissionRate >= 70 ? 'bg-yellow-100 text-yellow-800' :
          'bg-red-100 text-red-800'
        }`}>
          {submissionRate.toFixed(1)}%
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-1">
          {submission.marksRecorded ? (
            <span className="flex items-center gap-1 text-xs text-green-600">
              <CheckCircle2 className="w-3 h-3" />
              Marks Recorded
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-orange-600">
              <XCircle className="w-3 h-3" />
              Marks Pending
            </span>
          )}
        </div>
      </td>
      <td className="px-6 py-4 text-sm font-medium">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onSubmissionSelect(submission)}
            className="text-blue-600 hover:text-blue-900"
            title="View submission"
          >
            <Eye className="w-4 h-4" />
          </button>
          
          {onRecordMarks && (
            <button
              onClick={() => onRecordMarks(submission)}
              className="text-green-600 hover:text-green-900"
              title="Record marks"
            >
              <Calculator className="w-4 h-4" />
            </button>
          )}
          
          <button
            onClick={() => onEditSubmission(submission)}
            className="text-gray-600 hover:text-gray-900"
            title="Edit submission"
          >
            <Edit className="w-4 h-4" />
          </button>
          
          <button
            onClick={() => onDeleteSubmission(submission.id, taskName)}
            className="text-red-600 hover:text-red-900"
            title="Delete submission"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default SubmissionsList;